/**
 * GitHub Repos sub-route — displays the full GitHub inventory with filters.
 * Data is seeded into SurrealDB and queried via tRPC.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { createPageMeta, createCollectionJsonLd, PAGE_KEYWORDS } from '@/lib/meta';
import {
  Box,
  Container,
  Typography,
  Stack,
  Grid,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  Button,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import GitHubIcon from '@mui/icons-material/GitHub';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { pageColors, getGradient } from '@/theme/colors';
import { RepoCard } from '@/components/RepoCard';
import type { RepoOrigin, RepoIntent, RepoState } from '@/types/github-repo';
import { analytics } from '@/lib/analytics';

export const Route = createFileRoute('/projects/repos')({
  component: ReposPage,
  pendingMs: 150,
  loader: async ({ context }) => {
    context.queryClient.prefetchQuery(trpc.repos.themes.queryOptions(undefined, { staleTime: 60_000 }));

    await context.queryClient.ensureQueryData(trpc.repos.list.queryOptions({
      q: undefined,
      origin: 'all',
      intent: 'all',
      state: 'all',
      themes: [],
    }));
  },
  head: () => {
    const base = createPageMeta({
      title: 'GitHub Repos - Christian Battaglia',
      description: 'Full inventory of GitHub repositories by Christian Battaglia — original projects, maintained forks, and exploration forks across Rust, NGINX, audio, trading, and more.',
      path: '/projects/repos',
      image: '/og/projects.png',
      imageAlt: 'GitHub repositories — Christian Battaglia',
      keywords: PAGE_KEYWORDS.repos,
    });
    return {
      ...base,
      scripts: [
        createCollectionJsonLd({
          name: 'GitHub Repos — Christian Battaglia',
          description: 'Full inventory of GitHub repositories by Christian Battaglia.',
          path: '/projects/repos',
          keywords: PAGE_KEYWORDS.repos,
        }),
      ],
    };
  },
});

function ReposPage() {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const cyan = dark ? pageColors.projects.accent : pageColors.projects.primary;
  const cyanBg = dark ? 'rgba(103, 232, 249, 0.12)' : 'rgba(6, 182, 212, 0.1)';
  const cyanBgHover = dark ? 'rgba(103, 232, 249, 0.2)' : 'rgba(6, 182, 212, 0.2)';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrigin, setSelectedOrigin] = useState<RepoOrigin | 'all'>('all');
  const [selectedIntent, setSelectedIntent] = useState<RepoIntent | 'all'>('all');
  const [selectedState, setSelectedState] = useState<RepoState | 'all'>('all');
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);

  const queryInput = useMemo(() => ({
    q: searchQuery || undefined,
    origin: selectedOrigin,
    intent: selectedIntent,
    state: selectedState,
    themes: selectedThemes,
  }), [searchQuery, selectedOrigin, selectedIntent, selectedState, selectedThemes]);

  const { data: listData } = useQuery({
    ...trpc.repos.list.queryOptions(queryInput),
    placeholderData: keepPreviousData,
  });
  const { data: themesData } = useQuery(trpc.repos.themes.queryOptions(undefined, { staleTime: 60_000 }));

  const repos = listData?.repos ?? [];
  const allThemes = useMemo(() => (themesData ?? []).map(t => t.theme), [themesData]);

  const totalCount = listData?.totalUnfiltered ?? repos.length;
  const activeCount = listData?.activeCount ?? 0;
  const originalCount = listData?.originalCount ?? 0;

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!searchQuery) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      analytics.searchExecuted(searchQuery, 'repos', repos.length);
    }, 600);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery, repos.length]);

  const handleThemeToggle = (t: string) => {
    setSelectedThemes(prev =>
      prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
    );
    analytics.filterApplied('repos', 'theme', t);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedOrigin('all');
    setSelectedIntent('all');
    setSelectedState('all');
    setSelectedThemes([]);
    analytics.filtersCleared('repos');
  };

  const hasActiveFilters =
    searchQuery || selectedOrigin !== 'all' || selectedIntent !== 'all' || selectedState !== 'all' || selectedThemes.length > 0;

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: getGradient(pageColors.projects, dark ? 'dark' : 'light'),
          color: 'white',
          py: { xs: 6, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={3} alignItems={{ xs: 'center', md: 'flex-start' }} textAlign={{ xs: 'center', md: 'left' }}>
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  GitHub Repos
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ opacity: 0.95, fontWeight: 600, fontSize: { xs: '1rem', md: '1.25rem' } }}
                >
                  @cdbattags
                </Typography>

                <Card
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '100%',
                    boxShadow: 'none',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={1.5}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'white',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          fontSize: '0.75rem',
                          mb: 0.5,
                        }}
                      >
                        Overview
                      </Typography>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                          {totalCount} repos
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Total inventory
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                          {originalCount} original
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Created from scratch
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                          {activeCount} active
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Currently maintained
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <Stack spacing={3}>
                <Typography variant="h5" sx={{ opacity: 0.95, fontWeight: 600 }}>
                  The Full Inventory
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: '1.1rem', lineHeight: 1.8, opacity: 0.95 }}
                >
                  Every repo under @cdbattags — original projects, maintained forks, and exploration
                  forks across Rust, OpenResty/NGINX, audio/music, financial trading, React Native, and more.
                  Each entry includes what I was working on and why.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<GitHubIcon />}
                  href="https://github.com/cdbattags"
                  target="_blank"
                  rel="noopener noreferrer"
                  component="a"
                  sx={{
                    alignSelf: { xs: 'center', md: 'flex-start' },
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' },
                  }}
                >
                  View on GitHub
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Sub-Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Stack direction="row" spacing={0}>
            <Button
              component={Link}
              to="/projects"
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: 0,
                borderBottom: '2px solid transparent',
                color: 'text.secondary',
                fontWeight: 500,
                '&:hover': { bgcolor: cyanBg, color: cyan },
              }}
            >
              Featured Projects
            </Button>
            <Button
              component={Link}
              to="/projects/repos"
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: 0,
                borderBottom: '2px solid',
                borderColor: cyan,
                color: cyan,
                fontWeight: 600,
                '&:hover': { bgcolor: cyanBg },
              }}
            >
              GitHub Repos
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Search and Filters */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              placeholder="Search repos by name, description, or context..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Origin</InputLabel>
                  <Select
                    value={selectedOrigin}
                    label="Origin"
                    onChange={e => { const v = e.target.value as RepoOrigin | 'all'; setSelectedOrigin(v); analytics.filterApplied('repos', 'origin', v); }}
                  >
                    <MenuItem value="all">All Origins</MenuItem>
                    <MenuItem value="original">Original</MenuItem>
                    <MenuItem value="maintained-fork">Maintained Fork</MenuItem>
                    <MenuItem value="exploration-fork">Exploration Fork</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Intent</InputLabel>
                  <Select
                    value={selectedIntent}
                    label="Intent"
                    onChange={e => { const v = e.target.value as RepoIntent | 'all'; setSelectedIntent(v); analytics.filterApplied('repos', 'intent', v); }}
                  >
                    <MenuItem value="all">All Intents</MenuItem>
                    <MenuItem value="product">Product</MenuItem>
                    <MenuItem value="library">Library</MenuItem>
                    <MenuItem value="experiment">Experiment</MenuItem>
                    <MenuItem value="learning">Learning</MenuItem>
                    <MenuItem value="tooling">Tooling</MenuItem>
                    <MenuItem value="reference">Reference</MenuItem>
                    <MenuItem value="talk">Talk</MenuItem>
                    <MenuItem value="template">Template</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>State</InputLabel>
                  <Select
                    value={selectedState}
                    label="State"
                    onChange={e => { const v = e.target.value as RepoState | 'all'; setSelectedState(v); analytics.filterApplied('repos', 'state', v); }}
                  >
                    <MenuItem value="all">All States</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="paused">Paused</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                    <MenuItem value="abandoned">Abandoned</MenuItem>
                    <MenuItem value="want-to-revisit">Want to Revisit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {hasActiveFilters && (
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                    fullWidth
                    sx={{ height: '40px' }}
                  >
                    Clear Filters
                  </Button>
                </Grid>
              )}
            </Grid>

            {allThemes.length > 0 && (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <FilterIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Filter by Theme ({selectedThemes.length} selected)
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {allThemes.map(t => (
                    <Chip
                      key={t}
                      label={t}
                      size="small"
                      onClick={() => handleThemeToggle(t)}
                      sx={{
                        bgcolor: selectedThemes.includes(t) ? cyan : cyanBg,
                        color: selectedThemes.includes(t) ? (dark ? '#0a0a0a' : 'white') : cyan,
                        fontWeight: selectedThemes.includes(t) ? 600 : 500,
                        '&:hover': {
                          bgcolor: selectedThemes.includes(t) ? (dark ? '#22d3ee' : '#0891b2') : cyanBgHover,
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Paper>

        {/* Results Count */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {repos.length} of {totalCount} repos
            {hasActiveFilters && (
              <Typography component="span" sx={{ ml: 1, fontWeight: 600, color: cyan }}>
                (filtered)
              </Typography>
            )}
          </Typography>
        </Box>

        {/* Repos Grid */}
        {repos.length > 0 ? (
          <Grid container spacing={3}>
            {repos.map(repo => (
              <Grid key={repo.name} size={{ xs: 12, sm: 6, md: 4 }}>
                <RepoCard repo={repo} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No repos found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Try adjusting your search or filters
            </Typography>
            <Button variant="outlined" onClick={handleClearFilters} startIcon={<ClearIcon />}>
              Clear Filters
            </Button>
          </Paper>
        )}
      </Container>
    </>
  );
}
